import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, RefreshCw, ExternalLink, Calendar } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

type Series = {
  series_id: string;
  label: string | null;
  category: string | null;
  units: string | null;
  frequency: string | null;
  cbam_signal_key: string | null;
  refresh_cadence: string | null;
  active: boolean | null;
  last_observation_date: string | null;
  last_value: number | null;
  last_fetched_at: string | null;
  notes: string | null;
};

type Release = {
  id: string;
  release_id: number;
  release_name: string | null;
  release_date: string;
  release_time: string | null;
  passed: boolean | null;
  series_ids: string[] | null;
};

export default function ResearchFred() {
  const [tab, setTab] = useState("series");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const qc = useQueryClient();

  const { data: series, isLoading } = useQuery({
    queryKey: ["research_fred_series"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("research_fred_series")
        .select("series_id,label,category,units,frequency,cbam_signal_key,refresh_cadence,active,last_observation_date,last_value,last_fetched_at,notes")
        .order("category", { ascending: true })
        .order("series_id", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Series[];
    },
  });

  const { data: releases } = useQuery({
    queryKey: ["research_fred_release_dates"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("research_fred_release_dates")
        .select("id,release_id,release_name,release_date,release_time,passed,series_ids")
        .order("release_date", { ascending: true })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as Release[];
    },
  });

  const categories = useMemo(() => {
    const set = new Set<string>();
    (series ?? []).forEach((s) => s.category && set.add(s.category));
    return Array.from(set).sort();
  }, [series]);

  const filteredSeries = useMemo(() => {
    let list = series ?? [];
    if (cat !== "all") list = list.filter((s) => s.category === cat);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((s) =>
        s.series_id.toLowerCase().includes(n) ||
        (s.label ?? "").toLowerCase().includes(n) ||
        (s.cbam_signal_key ?? "").toLowerCase().includes(n)
      );
    }
    return list;
  }, [series, q, cat]);

  const toggleActive = useMutation({
    mutationFn: async ({ series_id, active }: { series_id: string; active: boolean }) => {
      const { error } = await (supabase as any)
        .from("research_fred_series")
        .update({ active })
        .eq("series_id", series_id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["research_fred_series"] }),
    onError: (e: any) => toast.error(e?.message ?? "Update failed"),
  });

  const refreshAll = useMutation({
    mutationFn: async (series_id?: string) => {
      const { data, error } = await supabase.functions.invoke("research-ingest-fred", {
        body: series_id ? { series_ids: [series_id] } : {},
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      toast.success(`Refreshed${data?.updated ? `: ${data.updated} obs` : ""}`);
      qc.invalidateQueries({ queryKey: ["research_fred_series"] });
      qc.invalidateQueries({ queryKey: ["research_fred_release_dates"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Refresh failed"),
  });

  const upcomingReleases = useMemo(() => {
    const now = new Date();
    return (releases ?? []).filter((r) => new Date(r.release_date) >= now).slice(0, 30);
  }, [releases]);

  const recentReleases = useMemo(() => {
    const now = new Date();
    return (releases ?? []).filter((r) => new Date(r.release_date) < now).slice(-30).reverse();
  }, [releases]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">FRED browser</h2>
          <p className="text-sm text-muted-foreground">
            Seeded FRED series mapped to CBAM signals. Toggle active to control which series feed predictions and the macro dashboard. Refresh pulls fresh observations and updates the upcoming release calendar.
          </p>
        </div>
        <Button onClick={() => refreshAll.mutate(undefined)} disabled={refreshAll.isPending}>
          {refreshAll.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Refresh all
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="series">Series <Badge variant="secondary" className="ml-2 h-4 text-[10px]">{series?.length ?? 0}</Badge></TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming releases <Badge variant="secondary" className="ml-2 h-4 text-[10px]">{upcomingReleases.length}</Badge></TabsTrigger>
          <TabsTrigger value="recent">Recent releases</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "series" && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Filter by series id, label, signal key…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="max-w-sm"
            />
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setCat("all")}
                className={`text-xs px-2 py-1 rounded border ${cat === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background text-muted-foreground hover:border-primary"}`}
              >all</button>
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`text-xs px-2 py-1 rounded border ${cat === c ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background text-muted-foreground hover:border-primary"}`}
                >{c}</button>
              ))}
            </div>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">
                {filteredSeries.length} series
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">Series</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="w-32">Signal</TableHead>
                    <TableHead className="w-20">Freq.</TableHead>
                    <TableHead className="w-36">Last obs.</TableHead>
                    <TableHead className="w-32">Last fetched</TableHead>
                    <TableHead className="w-20">Active</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-6">
                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && filteredSeries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-6">No entries</TableCell>
                    </TableRow>
                  )}
                  {filteredSeries.map((s) => (
                    <TableRow key={s.series_id}>
                      <TableCell className="text-xs font-mono">
                        <a
                          href={`https://fred.stlouisfed.org/series/${s.series_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 hover:underline"
                        >
                          {s.series_id}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                        {s.category && <div className="text-[10px] text-muted-foreground">{s.category}</div>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium">{s.label ?? "—"}</div>
                        {s.units && <div className="text-[10px] text-muted-foreground">{s.units}</div>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {s.cbam_signal_key ? (
                          <Badge variant="outline" className="text-[10px] font-mono">{s.cbam_signal_key}</Badge>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{s.frequency ?? "—"}</TableCell>
                      <TableCell className="text-xs">
                        {s.last_observation_date ? (
                          <>
                            <div>{format(new Date(s.last_observation_date), "dd/MM/yyyy")}</div>
                            {s.last_value != null && <div className="text-[10px] text-muted-foreground">{Number(s.last_value).toLocaleString()}</div>}
                          </>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {s.last_fetched_at ? format(new Date(s.last_fetched_at), "dd/MM/yyyy HH:mm") : "—"}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!s.active}
                          onCheckedChange={(checked) => toggleActive.mutate({ series_id: s.series_id, active: checked })}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Refresh this series"
                          disabled={refreshAll.isPending}
                          onClick={() => refreshAll.mutate(s.series_id)}
                        >
                          <RefreshCw className={`h-3 w-3 ${refreshAll.isPending && refreshAll.variables === s.series_id ? "animate-spin" : ""}`} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {(tab === "upcoming" || tab === "recent") && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Date</TableHead>
                  <TableHead className="w-20">Time</TableHead>
                  <TableHead>Release</TableHead>
                  <TableHead>Series</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(tab === "upcoming" ? upcomingReleases : recentReleases).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-6">No entries</TableCell>
                  </TableRow>
                )}
                {(tab === "upcoming" ? upcomingReleases : recentReleases).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        {format(new Date(r.release_date), "dd/MM/yyyy")}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.release_time ?? "—"}</TableCell>
                    <TableCell className="text-xs">
                      <div className="font-medium">{r.release_name ?? `Release ${r.release_id}`}</div>
                      {r.passed && <Badge variant="outline" className="mt-0.5 text-[10px]">passed</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="flex flex-wrap gap-1">
                        {(r.series_ids ?? []).slice(0, 8).map((sid) => (
                          <Badge key={sid} variant="outline" className="text-[10px] font-mono">{sid}</Badge>
                        ))}
                        {(r.series_ids ?? []).length > 8 && (
                          <span className="text-[10px] text-muted-foreground">+{(r.series_ids ?? []).length - 8}</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
